import Phaser from 'phaser';
import type { DoorOffer } from '../../core/floor';
import type { RewardType } from '../../data/schema';
import { REWARD_TEXT } from '../ui/state';

/** Couleur du portail selon la récompense. */
export const REWARD_COLOR: Record<RewardType, number> = {
  kamas: 0xf2c94c, xp: 0x9b7bff, marchand: 0xff9a3c, coffre: 0xc98a4b, fontaine: 0x5cc8ff, defi: 0xff4d4d,
};

const LABEL_DEPTH = 1_000_000;
const ARROW_DEPTH = 99_000;
const EDGE = 46;

export interface DoorViews { update(cam: Phaser.Cameras.Scene2D.Camera, time: number): void; destroy(): void; }

/**
 * Portes de récompense dessinées sur les sorties d'une salle générée : cercle lumineux au sol, colonne de lumière,
 * étiquette « touche + récompense » sous l'anneau ; une flèche au bord de l'écran indique chaque porte hors champ.
 */
export function buildDoorViews(scene: Phaser.Scene, doors: DoorOffer[]): DoorViews | null {
  const placed = doors.map((d, i) => ({ d, i })).filter((x) => x.d.pos);
  if (!placed.length) return null;
  const objs: Phaser.GameObjects.GameObject[] = [];
  const glows: Phaser.GameObjects.Ellipse[] = [];
  for (const { d, i } of placed) {
    const { x, y } = d.pos!;
    const color = REWARD_COLOR[d.reward];
    // sol : halo, anneau et colonne de lumière (sous les entités)
    const halo = scene.add.ellipse(x, y, 270, 135, color, 0.3).setDepth(-50_000);
    const ring = scene.add.ellipse(x, y, 190, 95, color, 0.35).setStrokeStyle(6, color, 1).setDepth(-49_999);
    const inner = scene.add.ellipse(x, y, 120, 60, 0xffffff, 0.35).setStrokeStyle(3, 0xffffff, 0.8).setDepth(-49_998);
    // faisceau : trois bandes de plus en plus étroites et claires
    const beams = [[150, 0.14, color], [90, 0.18, color], [36, 0.28, 0xffffff]] as const;
    const column = beams.map(([w, a, c]) => scene.add.rectangle(x, y - 4, w, 230, c, a).setOrigin(0.5, 1).setDepth(y - 1));
    glows.push(halo);
    // étiquette sous l'anneau : visible dès que la porte entre dans le champ, même tout en haut de l'écran
    const label = scene.add.text(x, y + 62, `${i + 1}  ${REWARD_TEXT[d.reward].title}`, {
      fontFamily: 'Trebuchet MS, sans-serif', fontSize: '22px', fontStyle: 'bold', color: '#fff6dc',
      backgroundColor: '#1b140cdd', padding: { x: 12, y: 6 }, stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5, 0).setDepth(LABEL_DEPTH);
    const bar = scene.add.rectangle(x, y + 62, label.width, 4, color, 1).setOrigin(0.5, 1).setDepth(LABEL_DEPTH);
    objs.push(halo, ring, inner, ...column, label, bar);
  }
  // flèches des portes hors champ (coordonnées écran)
  const arrows = scene.add.graphics().setScrollFactor(0).setDepth(ARROW_DEPTH);
  const tags = placed.map(({ i }) => scene.add.text(0, 0, `${i + 1}`, { fontFamily: 'Trebuchet MS, sans-serif', fontSize: '20px', fontStyle: 'bold', color: '#1b140c' }).setOrigin(0.5).setScrollFactor(0).setDepth(ARROW_DEPTH + 1).setVisible(false));
  objs.push(arrows, ...tags);
  return {
    update(cam, time) {
      const pulse = 0.3 + 0.12 * Math.sin(time / 260);
      for (const g of glows) g.setFillStyle(g.fillColor, pulse);
      arrows.clear();
      const view = cam.worldView, w = cam.width, h = cam.height;
      placed.forEach(({ d }, k) => {
        const tag = tags[k]!;
        const p = d.pos!;
        if (view.contains(p.x, p.y)) { tag.setVisible(false); return; }
        // point de la porte en coordonnées écran, ramené sur un rectangle à EDGE px du bord
        const sx = p.x - view.x, sy = p.y - view.y, cx = w / 2, cy = h / 2;
        const dx = sx - cx, dy = sy - cy;
        const t = Math.min((w / 2 - EDGE) / Math.max(1e-6, Math.abs(dx)), (h / 2 - EDGE) / Math.max(1e-6, Math.abs(dy)));
        const ax = cx + dx * t, ay = cy + dy * t, a = Math.atan2(dy, dx);
        const color = REWARD_COLOR[d.reward];
        const tip = (r: number, da: number): [number, number] => [ax + Math.cos(a + da) * r, ay + Math.sin(a + da) * r];
        const [x1, y1] = tip(34, 0), [x2, y2] = tip(26, 2.4), [x3, y3] = tip(26, -2.4);
        arrows.fillStyle(0x1b140c, 0.85).fillCircle(ax, ay, 22);
        arrows.fillStyle(color, 1).fillTriangle(x1, y1, x2, y2, x3, y3);
        arrows.fillStyle(color, 1).fillCircle(ax, ay, 17);
        arrows.lineStyle(3, 0x1b140c, 1).strokeCircle(ax, ay, 17);
        tag.setPosition(ax, ay).setVisible(true);
      });
    },
    destroy() { for (const o of objs) o.destroy(); },
  };
}
